// Environment configuration utility
// Centralizes all environment variable access for the frontend

export interface AppConfig {
  // API Configuration
  apiBaseUrl: string;
  apiVersion: string;
  
  // AWS Configuration
  awsRegion: string;
  awsAccountId: string;
  
  // Cognito Configuration
  cognitoUserPoolId: string;
  cognitoClientId: string;
  cognitoUserPoolDomain: string;
  cognitoIdentityPoolId: string;
  
  // S3 Configuration
  s3UserUploadBucket: string;
  
  // Development Configuration
  devMode: boolean;
  debug: boolean;
  
  // Test User Credentials
  testUserEmail: string;
  testUserPassword: string;
  
  // WebSocket Configuration
  wsUrl: string;
  
  // Feature Flags
  enableAuthBypass: boolean;
  enableMockData: boolean;
  
  // External Services
  githubClientId: string;
  analyticsId: string;
}

// Load configuration from environment variables
export const config: AppConfig = {
  // API Configuration
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  apiVersion: import.meta.env.VITE_API_VERSION || 'v1',
  
  // AWS Configuration
  awsRegion: import.meta.env.VITE_AWS_REGION,
  awsAccountId: import.meta.env.VITE_AWS_ACCOUNT_ID,
  
  // Cognito Configuration
  cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  cognitoUserPoolDomain: import.meta.env.VITE_COGNITO_USER_POOL_DOMAIN || '',
  cognitoIdentityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
  
  // S3 Configuration
  s3UserUploadBucket: import.meta.env.VITE_S3_USER_UPLOAD_BUCKET || '',
  
  // Development Configuration
  devMode: import.meta.env.VITE_DEV_MODE === 'true',
  debug: import.meta.env.VITE_DEBUG === 'true',
  
  // Test User Credentials
  testUserEmail: import.meta.env.VITE_TEST_USER_EMAIL,
  testUserPassword: import.meta.env.VITE_TEST_USER_PASSWORD || '',
  
  // WebSocket Configuration
  wsUrl: import.meta.env.VITE_WS_URL || '',
  
  // Feature Flags
  enableAuthBypass: import.meta.env.VITE_ENABLE_AUTH_BYPASS === 'true',
  enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  
  // External Services
  githubClientId: import.meta.env.VITE_GITHUB_CLIENT_ID || '',
  analyticsId: import.meta.env.VITE_ANALYTICS_ID || '',
};

// Validation function to check if required config is present
export const validateConfig = (): { isValid: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];
  
  // Check required fields
  if (!config.apiBaseUrl) missingFields.push('VITE_API_BASE_URL');
  if (!config.cognitoUserPoolId) missingFields.push('VITE_COGNITO_USER_POOL_ID');
  if (!config.cognitoClientId) missingFields.push('VITE_COGNITO_CLIENT_ID');
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

// Debug function to log current configuration (only in dev mode)
export const logConfig = (): void => {
  if (config.debug) {
    console.log('🔧 App Configuration:', {
      apiBaseUrl: config.apiBaseUrl,
      awsRegion: config.awsRegion,
      cognitoUserPoolId: config.cognitoUserPoolId,
      devMode: config.devMode,
      debug: config.debug,
      enableAuthBypass: config.enableAuthBypass,
      enableMockData: config.enableMockData,
    });
  }
};

// Export default config
export default config;
